export interface GuidanceNavLink {
  key: string;
  label: string;
  path: string;
  description?: string | null;
  active: boolean;
}

/**
 * Resolves playbook `links` keys into navigable chips, applying both:
 * 1. the registry's global `active` flag (suppresses a target everywhere at once
 *    when a surface is retired/renamed/not yet built)
 * 2. the current viewer's role-based route access (see guidanceRoleAccess.ts) —
 *    a role's default content can reference a target a specific role can't
 *    reach, so it's silently omitted rather than producing a dead link.
 *
 * Registry must come from the live `/guidance-hub/nav-links` API (Redux).
 * There is no static JSON fallback — empty registry → no chips until loaded.
 */
/**
 * Detects whether the current route matches one of the registry's targets,
 * so the Guide Drawer/Float card can show contextual "you're on this page"
 * content (design-note.md §5.2 / §6.3, confirmed against the live
 * prototype's LINK_DESTINATIONS + currentEbTab matching).
 *
 * Registry paths never include the `:orgSlug` prefix, so the current
 * pathname's first segment (the org slug) is stripped before comparing.
 * A registry entry with a `?tab=` query is only a match when the current
 * page's `tab` param is the same value; a tab-less entry only matches when
 * the current page has no `tab` param either — this is what disambiguates
 * `assessments` (`/pending-assessments`) from `levels-mix`
 * (`/pending-assessments?tab=level-matrix`).
 */
export const getCurrentGuidanceLinkKey = (
  pathname: string,
  search: string,
  registry: GuidanceNavLink[] = []
): string | null => {
  if (!registry.length) return null;
  const segments = pathname.split("/").filter(Boolean);
  const pathWithoutOrg =
    segments.length > 1 ? `/${segments.slice(1).join("/")}` : pathname;
  const currentTab = new URLSearchParams(search).get("tab");

  const match = registry.find(link => {
    if (!link.active) return false;
    const [linkPath, linkQuery] = link.path.split("?");
    if (linkPath !== pathWithoutOrg) return false;
    const linkTab = linkQuery
      ? new URLSearchParams(linkQuery).get("tab")
      : null;
    return linkTab ? linkTab === currentTab : !currentTab;
  });

  return match?.key || null;
};

export const resolveGuidanceLinks = (
  keys: string[] | undefined,
  accessibleKeys: string[],
  registry: GuidanceNavLink[] = []
): GuidanceNavLink[] => {
  if (!keys?.length || !registry.length) return [];
  const accessible = new Set(accessibleKeys);
  return keys
    .map(key => registry.find(link => link.key === key))
    .filter(
      (link): link is GuidanceNavLink =>
        !!link && link.active && accessible.has(link.key)
    );
};
